export default function GarageCard({ garage, vehicleCount, onClick, onOpenSettings, currentUserEmail }) {
  const collabCount = (garage.collaborators && garage.collaborators.length) || 0;
  const isOwner = garage.role !== 'collaborator' || garage.owner === currentUserEmail;
  const hasBanner = !!garage.bannerPhoto;

  return (
    <button
      onClick={onClick}
      className="card"
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: hasBanner ? 'column' : 'row',
        alignItems: hasBanner ? 'stretch' : 'center',
        gap: hasBanner ? 0 : 14,
        padding: hasBanner ? 0 : '16px 18px',
        cursor: 'pointer',
        textAlign: 'left',
        marginBottom: 10,
        overflow: 'hidden',
      }}
    >
      {/* Banner photo */}
      {hasBanner && (
        <div style={{
          width: '100%',
          height: 120,
          background: `url(${garage.bannerPhoto}) center/cover no-repeat`,
        }} />
      )}

      {/* Content row */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: hasBanner ? '12px 16px' : 0,
        width: '100%',
      }}>
        {/* Garage photo placeholder (only when no banner) */}
        {!hasBanner && (
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 'var(--radius-sm)',
              background: garage.bannerPhoto ? `url(${garage.bannerPhoto}) center/cover` : 'var(--bg-elevated)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              overflow: 'hidden',
            }}
          >
            {!garage.bannerPhoto && (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 21V9l9-7 9 7v12a1 1 0 01-1 1H4a1 1 0 01-1-1z" />
                <path d="M9 21V12h6v9" />
              </svg>
            )}
          </div>
        )}

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: '1rem' }}>{garage.name}</div>
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginTop: 2 }}>
            {vehicleCount} {vehicleCount === 1 ? 'vehicle' : 'vehicles'}
            {garage.location && (
              <span style={{ marginLeft: 8 }}>{garage.location}</span>
            )}
          </div>
          {/* Owner with collaborators → gray "Shared (N)" badge */}
          {isOwner && collabCount > 0 && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                marginTop: 4,
                padding: '2px 8px',
                borderRadius: 100,
                background: 'rgba(156, 163, 175, 0.15)',
                color: '#9CA3AF',
                fontSize: '0.6875rem',
                fontWeight: 600,
              }}
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 00-3-3.87" />
                <path d="M16 3.13a4 4 0 010 7.75" />
              </svg>
              Shared ({collabCount})
            </span>
          )}
          {/* Non-owner → blue "Shared with me" badge */}
          {!isOwner && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                marginTop: 4,
                padding: '2px 8px',
                borderRadius: 100,
                background: 'rgba(59, 130, 246, 0.12)',
                color: '#3b82f6',
                fontSize: '0.6875rem',
                fontWeight: 600,
              }}
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 00-3-3.87" />
                <path d="M16 3.13a4 4 0 010 7.75" />
              </svg>
              Shared with me
            </span>
          )}
        </div>

        {/* Gear icon */}
        {onOpenSettings && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onOpenSettings();
            }}
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: 'var(--bg-elevated)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.32 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
            </svg>
          </button>
        )}

        {/* Chevron */}
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </div>
    </button>
  );
}
